import React, { useCallback, useEffect, useState } from "react";
import { Box, Typography, Chip, Button, TextField, MenuItem, Select } from "@mui/material";
import { useTranslation } from "react-i18next";
import { searchInstallations } from "../../api/marketplaceApi";
import { formatDate } from "../../utils/i18nFormat";
import { KIND_ORDER, kindLabel, KIND_COLOR, shortImage, pageRange, lastPage } from "./installationHelpers";

const PAGE_SIZE = 25;
const fmt = (iso) => formatDate(iso);
const th = { textAlign: "left", padding: "6px 8px", color: "var(--text-muted)", fontWeight: 600 };
const td = { padding: "6px 8px", verticalAlign: "middle" };

// lockKind: tenant_admin sees only failed rows (to report them to support).
export default function InstallationTable({ accessToken, apps, title, lockKind }) {
  const { t } = useTranslation();
  const [qInput, setQInput] = useState("");
  const [q, setQ] = useState("");
  const [kind, setKind] = useState(lockKind || "");
  const [appId, setAppId] = useState("");
  const [page, setPage] = useState(1);
  const [data, setData] = useState({ items: [], total: 0, counts: {} });

  useEffect(() => {
    const timer = setTimeout(() => { setQ(qInput); setPage(1); }, 300);
    return () => clearTimeout(timer);
  }, [qInput]);

  const load = useCallback(async () => {
    try {
      setData(await searchInstallations(accessToken, { q, kind: lockKind || kind, appId, page, pageSize: PAGE_SIZE }));
    } catch (e) { /* keep the previous page; the next refresh retries */ }
  }, [accessToken, q, kind, lockKind, appId, page]);

  useEffect(() => {
    load();
    const timer = setInterval(load, 15000);
    return () => clearInterval(timer);
  }, [load]);

  const pick = (k) => { setKind(k === kind ? "" : k); setPage(1); };
  const counts = data.counts || {};
  const totalAll = Object.values(counts).reduce((a, b) => a + b, 0);
  if (!totalAll && !q && !kind && !appId) return null;

  return (
    <Box sx={{ mb: 3, p: 2, backgroundColor: "var(--bg-surface)", borderRadius: 2, border: "1px solid var(--border)" }}>
      <Typography variant="subtitle1" sx={{ mb: 1.5, color: "var(--text-primary)", fontWeight: 600 }}>{title}</Typography>
      <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", alignItems: "center", mb: 1.5 }}>
        <TextField size="small" placeholder={t("marketplace.installations.search")} value={qInput} onChange={(e) => setQInput(e.target.value)} sx={{ minWidth: 240 }} />
        <Select size="small" displayEmpty value={appId} onChange={(e) => { setAppId(e.target.value); setPage(1); }} sx={{ minWidth: 180 }}>
          <MenuItem value="">{t("marketplace.installations.allApps")}</MenuItem>
          {(apps || []).map((a) => <MenuItem key={a.slug} value={a.slug}>{a.name}</MenuItem>)}
        </Select>
        {!lockKind && KIND_ORDER.map((k) => (
          <Chip
            key={k} size="small" clickable onClick={() => pick(k)}
            label={`${kindLabel(k)}: ${counts[k] || 0}`}
            sx={{ backgroundColor: kind === k ? KIND_COLOR[k] : "transparent", color: kind === k ? "#fff" : "var(--text-primary)", border: `1px solid ${KIND_COLOR[k]}` }}
          />
        ))}
      </Box>
      <Box sx={{ overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13, color: "var(--text-primary)" }}>
          <thead>
            <tr>{["tenantEdge", "app", "status", "version", "lastUpdate", "note"].map((c) => <th key={c} style={th}>{t(`marketplace.installations.col.${c}`)}</th>)}</tr>
          </thead>
          <tbody>
            {data.items.map((r) => (
              <tr key={r.id} style={{ borderTop: "1px solid var(--border-subtle)" }}>
                <td style={td}>
                  {r.tenant_name && <span style={{ color: "var(--text-muted)" }}>{r.tenant_name} › </span>}
                  <span style={{ fontWeight: 600 }}>{r.edge_name}</span>
                </td>
                <td style={td}>{r.app_name}</td>
                <td style={td}><Chip size="small" label={r.kind in KIND_COLOR ? kindLabel(r.kind) : r.status} sx={{ backgroundColor: KIND_COLOR[r.kind], color: "#fff" }} /></td>
                <td style={td} title={r.installed_docker_image || ""}>
                  {shortImage(r.installed_docker_image)}
                  {["outdated", "updating", "waiting_offline"].includes(r.kind) ? ` → ${shortImage(r.current_docker_image)}` : ""}
                </td>
                <td style={td}>{fmt(r.completed_at)}</td>
                <td style={{ ...td, color: "var(--text-muted)", maxWidth: 260, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={r.error || ""}>
                  {r.error || (r.kind === "waiting_offline" ? t("marketplace.installations.appliedOnReconnect") : "")}
                </td>
              </tr>
            ))}
            {data.items.length === 0 && <tr><td style={{ ...td, color: "var(--text-muted)" }} colSpan={6}>{t("marketplace.installations.empty")}</td></tr>}
          </tbody>
        </table>
      </Box>
      <Box sx={{ display: "flex", gap: 1, alignItems: "center", justifyContent: "flex-end", mt: 1 }}>
        <Typography variant="caption" sx={{ color: "var(--text-muted)" }}>{pageRange(page, PAGE_SIZE, data.total)}</Typography>
        <Button size="small" disabled={page <= 1} onClick={() => setPage(page - 1)}>{t("common.previous")}</Button>
        <Button size="small" disabled={page >= lastPage(PAGE_SIZE, data.total)} onClick={() => setPage(page + 1)}>{t("common.next")}</Button>
      </Box>
    </Box>
  );
}
